/* ============================================================
   Poster preview renderer — shared with the builder (poster-builder.js).
   Renders a template's poster into a <canvas data-tpl-render> using the SAME
   compose logic (border → cover-fit + filtered image(s) → caption text) so the
   gallery/picker previews are pixel-identical to what the builder produces.
   Supports single + collage layouts (2up / grid / strip).
   Keep FILTERS / BORDERS / LAYOUTS / geom / drawText in sync with poster-builder.js.
   ============================================================ */
(function () {
  'use strict';
  // Several sections on one page may each include this file; init once.
  if (window.PeletonPosterRender) return;

  var FILTERS = {
    original: function () { return 'none'; },
    grayscale: function (t) { return 'grayscale(' + t + ')'; },
    soft: function (t) { return 'contrast(' + (1 - 0.1 * t) + ') brightness(' + (1 + 0.08 * t) + ') saturate(' + (1 - 0.3 * t) + ') sepia(' + (0.06 * t) + ')'; },
    mono: function (t) { return 'grayscale(' + t + ') contrast(' + (1 + 0.4 * t) + ') brightness(' + (1 + 0.06 * t) + ')'; },
    warm: function (t) { return 'sepia(' + (0.4 * t) + ') saturate(' + (1 + 0.35 * t) + ') brightness(' + (1 + 0.04 * t) + ') contrast(' + (1 + 0.02 * t) + ')'; }
  };
  function filterStr(name, t) { var f = FILTERS[name]; return f ? f(t == null ? 1 : t) : 'none'; }

  // ctx.filter isn't supported on older iOS Safari (< 17). Detect it, and provide
  // a pixel-based fallback so filters still work (and bake into the print export)
  // everywhere. Same visual result as the CSS FILTERS above.
  var FILTER_OK = (function () { try { var c = document.createElement('canvas').getContext('2d'); return typeof c.filter !== 'undefined'; } catch (e) { return false; } })();
  function clamp255(v) { return v < 0 ? 0 : v > 255 ? 255 : v; }
  function applyPixelFilter(d, name, t) {
    if (name === 'original' || !t) return;
    for (var i = 0; i < d.length; i += 4) {
      var r = d[i], g = d[i + 1], b = d[i + 2], lum;
      if (name === 'grayscale') {
        lum = 0.2126 * r + 0.7152 * g + 0.0722 * b; r += (lum - r) * t; g += (lum - g) * t; b += (lum - b) * t;
      } else if (name === 'mono') {
        lum = 0.2126 * r + 0.7152 * g + 0.0722 * b; r = g = b = lum;
        var c = 1 + 0.4 * t, br = 1 + 0.06 * t;
        r = (r - 128) * c + 128; g = (g - 128) * c + 128; b = (b - 128) * c + 128; r *= br; g *= br; b *= br;
      } else if (name === 'warm') {
        var wt = 0.4 * t, sr = r * 0.393 + g * 0.769 + b * 0.189, sg = r * 0.349 + g * 0.686 + b * 0.168, sb = r * 0.272 + g * 0.534 + b * 0.131;
        r += (sr - r) * wt; g += (sg - g) * wt; b += (sb - b) * wt;
        var sat = 1 + 0.35 * t; lum = 0.2126 * r + 0.7152 * g + 0.0722 * b; r = lum + (r - lum) * sat; g = lum + (g - lum) * sat; b = lum + (b - lum) * sat;
        var wbr = 1 + 0.04 * t, wc = 1 + 0.02 * t; r *= wbr; g *= wbr; b *= wbr; r = (r - 128) * wc + 128; g = (g - 128) * wc + 128; b = (b - 128) * wc + 128;
      } else if (name === 'soft') {
        var cc = 1 - 0.1 * t; r = (r - 128) * cc + 128; g = (g - 128) * cc + 128; b = (b - 128) * cc + 128;
        var sbr = 1 + 0.08 * t; r *= sbr; g *= sbr; b *= sbr;
        var ss = 1 - 0.3 * t; lum = 0.2126 * r + 0.7152 * g + 0.0722 * b; r = lum + (r - lum) * ss; g = lum + (g - lum) * ss; b = lum + (b - lum) * ss;
        var stp = 0.06 * t, sr2 = r * 0.393 + g * 0.769 + b * 0.189, sg2 = r * 0.349 + g * 0.686 + b * 0.168, sb2 = r * 0.272 + g * 0.534 + b * 0.131;
        r += (sr2 - r) * stp; g += (sg2 - g) * stp; b += (sb2 - b) * stp;
      } else { continue; }
      d[i] = clamp255(r); d[i + 1] = clamp255(g); d[i + 2] = clamp255(b);
    }
  }
  // Draw an image into g, clipped to (cx,cy,cw,ch), with a named filter applied —
  // via ctx.filter where supported, else pixel processing (untainted canvases only).
  function drawFiltered(g, img, dx, dy, dw, dh, cx, cy, cw, ch, name, t) {
    if (FILTER_OK) {
      g.filter = filterStr(name, t); g.drawImage(img, dx, dy, dw, dh); g.filter = 'none'; return;
    }
    // Pixel fallback (old iOS). Only the image sub-rect (cx..cw) is read/written,
    // so the pattern/border background is never touched — filter affects the photo only.
    g.drawImage(img, dx, dy, dw, dh);
    if (name === 'original' || !t) return;
    // getImageData/putImageData ignore the ctx transform (they use device pixels),
    // so map the user-space clip rect through the current transform.
    var a = 1, d = 1, e = 0, f = 0;
    try { var m = g.getTransform(); a = m.a; d = m.d; e = m.e; f = m.f; } catch (err) {}
    // Intersect with the canvas: the print is drawn in bands, so most of the photo
    // usually lies outside the current band (and reading it would cost memory).
    var x0 = Math.max(0, Math.round(cx * a + e)), y0 = Math.max(0, Math.round(cy * d + f));
    var x1 = Math.min(g.canvas.width, Math.round((cx + cw) * a + e)), y1 = Math.min(g.canvas.height, Math.round((cy + ch) * d + f));
    var rx = x0, ry = y0, rw = x1 - x0, rh = y1 - y0;
    if (rw < 1 || rh < 1) return;
    try { var id = g.getImageData(rx, ry, rw, rh); applyPixelFilter(id.data, name, t); g.putImageData(id, rx, ry); } catch (e2) {}
  }
  window.PeletonFilters = { ok: FILTER_OK, apply: applyPixelFilter, drawFiltered: drawFiltered, str: filterStr };

  // Load an image trying CORS first (so the pixel filter can read it on old iOS),
  // falling back to a plain load if the host sends no CORS header (so it still
  // displays — just unfiltered on old iOS). Never leaves an image un-loaded.
  function loadImage(url, onload, onerror) {
    var im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = function () { onload(im); };
    im.onerror = function () {
      var im2 = new Image();
      im2.onload = function () { onload(im2); };
      im2.onerror = function () { if (onerror) onerror(); };
      im2.src = url;
    };
    im.src = url;
  }
  window.PeletonLoadImage = loadImage;

  var BORDERS = { none: null, white: '#ffffff', beige: '#e9e1d3', black: '#111111' };

  /* ---------- decorative background patterns (mat / passepartout) ---------- */
  var PATTERNS = {
    dots:    { bg: '#f4efe4', ink: '#3b3733', label: 'Prikker' },
    gingham: { bg: '#ffffff', ink: '#8bb59a', label: 'Ternet grøn' },
    blue:    { bg: '#ffffff', ink: '#9cbcdd', label: 'Ternet blå' },
    hearts:  { bg: '#f8f0ea', ink: '#cf8f8f', label: 'Hjerter' },
    floral:  { bg: '#f4efe4', ink: '#b39a63', label: 'Blomster' },
    stars:   { bg: '#f4efe4', ink: '#c9a84c', label: 'Stjerner' },
    check:   { bg: '#ffffff', ink: '#3a3a3a', label: 'Ternet sort' },
    browndot:{ bg: '#f2ece1', ink: '#7a5a43', label: 'Brune prikker' },
    stripegreen: { bg: '#f6f3ea', ink: '#7d8a5c', label: 'Striber grøn' },
    stripepink:  { bg: '#f9f3ef', ink: '#d8a5a5', label: 'Striber rosa' },
    checkterra:  { bg: '#f6f1e6', ink: '#a86a4b', label: 'Ternet terracotta' }
  };
  function hasPattern(name) { return !!(name && name !== 'none' && PATTERNS[name]); }

  // Shared linen texture overlaid on every drawn pattern for a woven, tactile
  // feel while keeping the geometry mathematically perfect. crossOrigin keeps the
  // canvas untainted so the print export (toBlob) still works.
  var TEXTURE_URL = 'https://cdn.shopify.com/s/files/1/0475/9152/2459/files/linen-texture.jpg?v=1784369045';
  var TEXTURE_ALPHA = 0.55;
  var _tex = null, _texReady = false, _texCbs = [];
  function setTexture(url) {
    if (!url || _tex) return;
    var im = new Image();
    im.crossOrigin = 'anonymous';
    im.onload = function () { _tex = im; _texReady = true; for (var i = 0; i < _texCbs.length; i++) _texCbs[i](); };
    im.onerror = function () { _tex = null; _texReady = false; };
    im.src = url;
  }
  function onTexture(cb) { if (_texReady) cb(); else _texCbs.push(cb); }
  // Multiply the linen texture over a rect (cover-fit). Call inside the clip.
  function overlayTexture(g, X, Y, W, H) {
    if (!_texReady || !_tex) return;
    var tw = _tex.naturalWidth, th = _tex.naturalHeight;
    var cover = Math.max(W / tw, H / th), dw = tw * cover, dh = th * cover;
    var prevOp = g.globalCompositeOperation, prevA = g.globalAlpha;
    g.globalCompositeOperation = 'multiply'; g.globalAlpha = TEXTURE_ALPHA;
    g.drawImage(_tex, X + (W - dw) / 2, Y + (H - dh) / 2, dw, dh);
    g.globalCompositeOperation = prevOp; g.globalAlpha = prevA;
  }
  function heartPath(g, cx, cy, s) {
    g.beginPath();
    g.moveTo(cx, cy + s * 0.35);
    g.bezierCurveTo(cx - s, cy - s * 0.5, cx - s * 0.5, cy - s * 1.15, cx, cy - s * 0.4);
    g.bezierCurveTo(cx + s * 0.5, cy - s * 1.15, cx + s, cy - s * 0.5, cx, cy + s * 0.35);
    g.closePath(); g.fill();
  }
  function flower(g, cx, cy, r, ink) {
    g.fillStyle = ink;
    for (var i = 0; i < 5; i++) { var a = i / 5 * 6.2832 - 1.5708; g.beginPath(); g.arc(cx + Math.cos(a) * r, cy + Math.sin(a) * r, r * 0.6, 0, 6.2832); g.fill(); }
    g.fillStyle = '#e7c86b'; g.beginPath(); g.arc(cx, cy, r * 0.5, 0, 6.2832); g.fill();
  }
  function star(g, cx, cy, r) {
    g.beginPath();
    for (var i = 0; i < 5; i++) {
      var a = -1.5708 + i * 1.25664; g.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
      var a2 = a + 0.62832; g.lineTo(cx + Math.cos(a2) * r * 0.45, cy + Math.sin(a2) * r * 0.45);
    }
    g.closePath(); g.fill();
  }
  // Fill rect (X,Y,W,H) with a tiled pattern. `unit` = target tile spacing (scaled).
  // Cells are fit to an integer count so the pattern is centred / symmetric on all
  // edges (equal half-cell margin everywhere).
  function drawPattern(g, name, X, Y, W, H, unit) {
    var p = PATTERNS[name]; if (!p) return;
    var step = Math.max(6, unit);
    // Force an ODD number of cells so the pattern is mirror-symmetric about the
    // centre — both edges match (e.g. gingham is green on both left AND right).
    var cols = Math.max(3, Math.round(W / step)); if (cols % 2 === 0) cols += 1;
    var rows = Math.max(3, Math.round(H / step)); if (rows % 2 === 0) rows += 1;
    var sx = W / cols, sy = H / rows, u = Math.min(sx, sy);
    var i, j, cx, cy;
    g.save();
    g.beginPath(); g.rect(X, Y, W, H); g.clip();
    g.fillStyle = p.bg; g.fillRect(X, Y, W, H);
    if (/^stripe/.test(name)) {
      // Vertical bands only. The odd column count above keeps the pattern
      // symmetric, so the poster starts and ends on the same colour.
      g.fillStyle = p.ink;
      for (i = 0; i < cols; i++) if (i % 2 === 0) g.fillRect(X + i * sx, Y, sx, H);
    } else if (name === 'gingham' || name === 'blue' || name === 'check' || name === 'checkterra') {
      // Symmetric weave: bands on every other cell, mirrored so both edges match.
      g.fillStyle = p.ink; g.globalAlpha = (name === 'check') ? 0.42 : 0.5;
      for (i = 0; i < cols; i++) if (i % 2 === 0) g.fillRect(X + i * sx, Y, sx, H);
      for (j = 0; j < rows; j++) if (j % 2 === 0) g.fillRect(X, Y + j * sy, W, sy);
      g.globalAlpha = 1;
    } else if (name === 'dots' || name === 'browndot') {
      g.fillStyle = p.ink; var r = u * 0.14;
      for (j = 0; j < rows; j++) for (i = 0; i < cols; i++) { g.beginPath(); g.arc(X + (i + 0.5) * sx, Y + (j + 0.5) * sy, r, 0, 6.2832); g.fill(); }
    } else if (name === 'hearts') {
      g.fillStyle = p.ink;
      for (j = 0; j < rows; j++) for (i = 0; i < cols; i++) heartPath(g, X + (i + 0.5) * sx, Y + (j + 0.5) * sy, u * 0.24);
    } else if (name === 'floral') {
      for (j = 0; j < rows; j++) for (i = 0; i < cols; i++) flower(g, X + (i + 0.5) * sx, Y + (j + 0.5) * sy, u * 0.17, p.ink);
    } else if (name === 'stars') {
      g.fillStyle = p.ink;
      for (j = 0; j < rows; j++) for (i = 0; i < cols; i++) star(g, X + (i + 0.5) * sx, Y + (j + 0.5) * sy, u * 0.26);
    }
    // Linen texture only on the gingham/check patterns (woven fabric look).
    if (name === 'gingham' || name === 'blue' || name === 'check' || name === 'checkterra' || /^stripe/.test(name)) overlayTexture(g, X, Y, W, H);
    g.restore();
  }
  window.PeletonPatterns = { PATTERNS: PATTERNS, has: hasPattern, draw: drawPattern, bg: function (n) { return PATTERNS[n] ? PATTERNS[n].bg : '#ffffff'; }, onTexture: onTexture, ready: function () { return _texReady; } };

  var G0 = 0.03;
  var R_NOM = 0.707;
  function grid2x2(k, ar) {
    var w = ((1 - G0) / 2) * k, g = G0 * k;
    var h = ar ? w * R_NOM / ar : w;
    var x0 = (1 - (2 * w + g)) / 2, y0 = (1 - (2 * h + g)) / 2;
    return [
      { x: x0, y: y0, w: w, h: h },
      { x: x0 + w + g, y: y0, w: w, h: h },
      { x: x0, y: y0 + h + g, w: w, h: h },
      { x: x0 + w + g, y: y0 + h + g, w: w, h: h }
    ];
  }
  var LAYOUTS = {
    single: [{ x: 0, y: 0, w: 1, h: 1 }],
    '2up': [
      { x: 0, y: 0, w: 1, h: (1 - G0) / 2 },
      { x: 0, y: (1 + G0) / 2, w: 1, h: (1 - G0) / 2 }
    ],
    grid: [
      { x: 0, y: 0, w: (1 - G0) / 2, h: (1 - G0) / 2 },
      { x: (1 + G0) / 2, y: 0, w: (1 - G0) / 2, h: (1 - G0) / 2 },
      { x: 0, y: (1 + G0) / 2, w: (1 - G0) / 2, h: (1 - G0) / 2 },
      { x: (1 + G0) / 2, y: (1 + G0) / 2, w: (1 - G0) / 2, h: (1 - G0) / 2 }
    ],
    'grid-small': grid2x2(0.78, null),
    'grid-wide': grid2x2(1, 1.5),
    'grid-wide-small': grid2x2(0.78, 1.5),
    strip: (function () {
      var n = 3, g = 0.03, h = (1 - g * (n - 1)) / n, a = [];
      for (var i = 0; i < n; i++) a.push({ x: 0.14, y: i * (h + g), w: 0.72, h: h });
      return a;
    })(),
    photobooth: (function () {
      var n = 4, g = 0.02, top = 0.025, avail = 1 - 2 * top;
      var h = (avail - g * (n - 1)) / n, ar = 0.8;
      var w = Math.min(0.62, h * ar / R_NOM), x0 = (1 - w) / 2, a = [];
      for (var i = 0; i < n; i++) a.push({ x: x0, y: top + i * (h + g), w: w, h: h });
      return a;
    })()
  };

  function fontFamily(font) { return font === 'classic' ? 'Georgia, "Times New Roman", serif' : '"Helvetica Neue", Arial, sans-serif'; }
  function fontWeight(font) { return font === 'minimal' ? '300' : (font === 'classic' ? '500' : '600'); }

  function geom(W, H, border, hasText, pattern) {
    var pat = hasPattern(pattern), m = Math.min(W, H);
    var ob = border === 'none' ? 0 : Math.round(m * 0.045);
    var bt = pat ? ob + Math.round(m * 0.085) : (border === 'none' ? 0 : Math.round(m * 0.06));
    var iw = W - 2 * bt, ih = H - 2 * bt;
    var capH = hasText ? Math.round(Math.max(ih * 0.16, pat ? m * 0.19 : 0)) : 0;
    return { W: W, H: H, ob: ob, capH: capH, capEnd: pat ? bt + ih : H, img: { x: bt, y: bt, w: iw, h: ih - capH }, cap: { x: bt, y: bt + ih - capH, w: iw, h: capH } };
  }

  // Same shrink-to-fit as fitLine() in poster-builder.js — keep in sync.
  function fitLine(g, text, weight, size, fam, track, maxW) {
    var fs = size;
    for (var i = 0; i < 4; i++) {
      g.font = weight + ' ' + fs + 'px ' + fam;
      try { g.letterSpacing = (fs * track) + 'px'; } catch (e) {}
      var w = g.measureText(text).width;
      if (w <= maxW) break;
      fs *= Math.max(0.5, maxW / w) * 0.99;
    }
    return fs;
  }
  function drawText(g, G, font, headline, subtitle, border) {
    var ink = border === 'black' ? '#ffffff' : '#1b1b1b';
    var fam = fontFamily(font), wt = fontWeight(font);
    var tb = Math.min(G.W, G.H), hSize = tb * 0.05, sSize = tb * 0.03;
    var cx = (G.cap.x + G.cap.w / 2), cy = (G.cap.y + G.capEnd) / 2;   // band + same-colour bottom border
    var maxW = G.cap.w * 0.9, hTrack = font === 'minimal' ? 0.2 : 0.05;
    var hl = (headline || '').toUpperCase(), st = (subtitle || '').toUpperCase();
    g.fillStyle = ink; g.textAlign = 'center'; g.textBaseline = 'middle';
    if (hl && st) {
      fitLine(g, hl, wt, hSize, fam, hTrack, maxW); g.fillText(hl, cx, cy - sSize * 0.85);
      fitLine(g, st, '400', sSize, fam, 0.12, maxW); g.fillText(st, cx, cy + hSize * 0.75);
    } else if (hl || st) {
      var only = hl || st;
      // (a lone subtitle keeps the headline's tracking, as before)
      fitLine(g, only, hl ? wt : '400', hl ? hSize : sSize, fam, hl ? hTrack : hTrack * hSize / sSize, maxW); g.fillText(only, cx, cy);
    }
    try { g.letterSpacing = '0px'; } catch (e) {}
  }

  // opts: { imageUrls: [..], filter, border, layout, headline, subtitle, font }
  function renderPoster(canvas, opts) {
    if (!canvas) return;
    var layout = (opts.layout && LAYOUTS[opts.layout]) ? opts.layout : 'single';
    var slots = LAYOUTS[layout];
    var urls = opts.imageUrls || [];

    function paint(images) {
      var W = canvas.clientWidth, H = canvas.clientHeight;
      if (!W || !H) return;
      var dpr = window.devicePixelRatio || 1;
      canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
      var g = canvas.getContext('2d');
      g.setTransform(dpr, 0, 0, dpr, 0, 0);
      g.imageSmoothingQuality = 'high';
      var border = opts.border || 'white';
      var pattern = opts.pattern;
      var pat = hasPattern(pattern);
      var hasText = !!(((opts.headline || '').trim()) || ((opts.subtitle || '').trim()));
      var G = geom(W, H, border, hasText, pattern);
      g.clearRect(0, 0, W, H);
      g.fillStyle = '#ffffff'; g.fillRect(0, 0, W, H);
      var bc = BORDERS[border];
      if (bc) { g.fillStyle = bc; g.fillRect(0, 0, W, H); }
      if (pat) { drawPattern(g, pattern, G.ob, G.ob, W - 2 * G.ob, H - 2 * G.ob, W * 0.055); }
      for (var i = 0; i < slots.length; i++) {
        var s = slots[i];
        var r = { x: G.img.x + s.x * G.img.w, y: G.img.y + s.y * G.img.h, w: s.w * G.img.w, h: s.h * G.img.h };
        var img = images[i] || (images.length === 1 ? images[0] : null);
        if (img) {
          var cover = Math.max(r.w / img.naturalWidth, r.h / img.naturalHeight);
          var dw = img.naturalWidth * cover, dh = img.naturalHeight * cover;
          g.save();
          g.beginPath(); g.rect(r.x, r.y, r.w, r.h); g.clip();
          drawFiltered(g, img, r.x + (r.w - dw) / 2, r.y + (r.h - dh) / 2, dw, dh, r.x, r.y, r.w, r.h, opts.filter || 'original', 1);
          g.restore();
        } else {
          g.fillStyle = 'rgba(0,0,0,0.05)'; g.fillRect(r.x, r.y, r.w, r.h);
        }
      }
      if (G.capH > 0) {
        g.fillStyle = pat ? window.PeletonPatterns.bg(pattern) : (bc || '#ffffff');
        g.fillRect(G.cap.x, G.cap.y, G.cap.w, G.cap.h);
        drawText(g, G, opts.font || 'modern', opts.headline, opts.subtitle, pat ? 'none' : border);
      }
    }

    if (!urls.length) { paint([]); return; }
    var loaded = [], remaining = urls.length;
    urls.forEach(function (url, idx) {
      loadImage(url,
        function (im) { loaded[idx] = im; if (--remaining === 0) { canvas._pbImgs = loaded; paint(loaded); } },
        function () { loaded[idx] = null; if (--remaining === 0) { canvas._pbImgs = loaded; paint(loaded); } });
    });
  }

  function optsFrom(el) {
    var raw = el.getAttribute('data-images') || el.getAttribute('data-image') || '';
    var urls = raw.split(/[\n|]+/).map(function (s) { return s.trim(); }).filter(Boolean);
    return {
      imageUrls: urls,
      filter: el.getAttribute('data-filter'),
      border: el.getAttribute('data-border'),
      pattern: el.getAttribute('data-pattern'),
      layout: el.getAttribute('data-layout'),
      headline: el.getAttribute('data-headline'),
      subtitle: el.getAttribute('data-subtitle'),
      font: el.getAttribute('data-font') || 'modern'
    };
  }
  function renderEl(el) { renderPoster(el, optsFrom(el)); }

  // Render small pattern swatch canvases (picker chips) so they match exactly.
  function initSwatches() {
    var sw = document.querySelectorAll('[data-pattern-swatch]');
    for (var i = 0; i < sw.length; i++) {
      (function (cv) {
        var name = cv.getAttribute('data-pattern-swatch');
        var W = cv.clientWidth || 40, H = cv.clientHeight || 40;
        var dpr = window.devicePixelRatio || 1;
        cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr);
        var g = cv.getContext('2d'); g.setTransform(dpr, 0, 0, dpr, 0, 0);
        if (hasPattern(name)) drawPattern(g, name, 0, 0, W, H, W * 0.34);
        else { g.fillStyle = '#ffffff'; g.fillRect(0, 0, W, H); }
      })(sw[i]);
    }
  }

  function initAll() {
    setTexture(window.__peletonPatternTexture || TEXTURE_URL);
    initSwatches();
    var els = document.querySelectorAll('[data-tpl-render]');
    // Re-render swatches + preview canvases once the linen texture is ready.
    onTexture(function () {
      initSwatches();
      for (var i = 0; i < els.length; i++) if (els[i]._pbImgs || els[i]._pbImg) renderEl(els[i]);
    });
    // Discourage saving/copying the poster previews (canvas → "Save Image As").
    for (var n = 0; n < els.length; n++) {
      els[n].addEventListener('contextmenu', function (e) { e.preventDefault(); });
      els[n].addEventListener('dragstart', function (e) { e.preventDefault(); });
    }
    if (!els.length) return;
    if (window.IntersectionObserver) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) { if (en.isIntersecting) { renderEl(en.target); io.unobserve(en.target); } });
      }, { rootMargin: '300px' });
      for (var i = 0; i < els.length; i++) io.observe(els[i]);
    } else {
      for (var j = 0; j < els.length; j++) renderEl(els[j]);
    }
    if (window.ResizeObserver) {
      var ro = new ResizeObserver(function (entries) {
        entries.forEach(function (en) { if (en.target._pbImgs) renderPoster(en.target, optsFrom(en.target)); });
      });
      for (var k = 0; k < els.length; k++) ro.observe(els[k]);
    }
  }

  /* ---------- template carousels (groups with more than 4 templates) ----------
     The track is a plain scroll-snap flex row, so swipe and keyboard scrolling
     work with no JS at all; this only wires the desktop arrows and keeps their
     disabled state in sync with the scroll position. */
  function initCarousels() {
    var tracks = document.querySelectorAll('[data-pcar]');
    for (var i = 0; i < tracks.length; i++) (function (track) {
      var row = track.parentNode;
      if (!row) return;
      var prev = row.querySelector('[data-pcar-prev]'), next = row.querySelector('[data-pcar-next]');
      if (!prev || !next) return;
      // Card pitch = distance between two card origins, so the gap is included
      // whatever the clamp() resolves to at this viewport.
      function step() {
        var a = track.children[0], b = track.children[1];
        if (!a) return 240;
        if (b) return Math.abs(b.offsetLeft - a.offsetLeft) || a.offsetWidth;
        return a.offsetWidth;
      }
      function update() {
        var max = track.scrollWidth - track.clientWidth;
        var overflowing = max > 2;
        // Sub-pixel widths and scroll-snap leave a fraction of a pixel at each
        // end, so an exact comparison never reports "done". 4px of slack is well
        // under one card and safely past the rounding.
        var atStart = track.scrollLeft <= 4;
        var atEnd = track.scrollLeft >= max - 4;
        set(prev, !overflowing || atStart);
        set(next, !overflowing || atEnd);
      }
      // Hidden via a class, not just [disabled] — an arrow that can't do anything
      // should not be on screen at all, whatever else styles disabled buttons.
      function set(btn, off) {
        btn.disabled = off;
        btn.classList.toggle('is-off', off);
      }
      function go(dir) {
        // Advance by a whole number of cards, keeping one card of context.
        var s = step(), n = Math.max(1, Math.floor(track.clientWidth / s) - 1);
        track.scrollBy({ left: dir * s * n, behavior: 'smooth' });
      }
      prev.addEventListener('click', function () { go(-1); });
      next.addEventListener('click', function () { go(1); });
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      // The cards are canvases sized after layout, so scrollWidth is not final at
      // init — watch the track and its first card, and re-check once images settle.
      if (window.ResizeObserver) {
        try {
          var ro = new ResizeObserver(update);
          ro.observe(track);
          if (track.children[0]) ro.observe(track.children[0]);
        } catch (e) {}
      }
      window.addEventListener('load', update);
      setTimeout(update, 300);
      setTimeout(update, 1200);
      update();
    })(tracks[i]);
  }

  /* ---------- cart upload budget ----------
     Shopify's /cart/add.js rejects the WHOLE multipart request with 413 once it
     passes ~20 MB, however it is split across files (measured Oct 2026: one 19 MB
     file → accepted, 21 MB → 413, 12 + 12 MB → 413). Every builder sends a print
     file + preview (+ the customer's originals), so they all fit to this budget. */
  var UPLOAD_BUDGET = 18 * 1048576;   // ~2 MB headroom for text fields + multipart overhead
  var PRINT_MAX = 12 * 1048576;       // print master cap, so originals keep a fair share
  function canvasJpeg(c, q) { return new Promise(function (res) { if (c.toBlob) c.toBlob(res, 'image/jpeg', q); else res(null); }); }
  // JPEG no larger than maxBytes: steps quality down from q0 and returns the first
  // encode that fits (or the smallest one tried, so there is always a file).
  function encodeJpeg(c, maxBytes, q0) {
    var qs = [q0 || 0.92, 0.88, 0.84, 0.8, 0.75, 0.7];
    var i = 0;
    function step(best) {
      if (i >= qs.length) return Promise.resolve(best);
      return canvasJpeg(c, qs[i++]).then(function (b) {
        if (!b) return best;
        if (!maxBytes || b.size <= maxBytes) return b;
        return step(best && best.size < b.size ? best : b);
      });
    }
    return step(null);
  }
  function decodePhoto(file) {
    if (window.createImageBitmap) {
      return createImageBitmap(file, { imageOrientation: 'from-image' }).catch(function () { return createImageBitmap(file); });
    }
    return new Promise(function (res, rej) {
      var url = URL.createObjectURL(file), im = new Image();
      im.onload = function () { res(im); setTimeout(function () { URL.revokeObjectURL(url); }, 0); };
      im.onerror = function (e) { URL.revokeObjectURL(url); rej(e); };
      im.src = url;
    });
  }
  // Largest canvas this device will really back, at most w×h (iOS silently caps
  // the area at ~16.7 MP and hands back a blank buffer instead of throwing).
  function fitCanvas(w, h) {
    var c = document.createElement('canvas'), k = 1;
    for (var n = 0; n < 12; n++) {
      c.width = Math.max(1, Math.round(w * k)); c.height = Math.max(1, Math.round(h * k));
      var g = c.getContext('2d');
      try {
        g.fillStyle = 'rgb(1,2,3)'; g.fillRect(c.width - 1, c.height - 1, 1, 1);
        var p = g.getImageData(c.width - 1, c.height - 1, 1, 1).data;
        if (p[0] === 1 && p[1] === 2 && p[2] === 3) return { c: c, k: k };
      } catch (e) {}
      k *= 0.85;
    }
    return null;
  }
  // Re-encode a customer photo so it fits maxBytes, giving up as little as possible:
  // first at full size with stepped-down JPEG quality (phone JPEGs are stored at
  // ~q95, so this alone usually halves them); only if that still doesn't fit is it
  // scaled down step by step. A failed cart is worse than a smaller backup — the
  // print master already carries the full-resolution render.
  // Resolves { file, note } — note says what was done, for the order.
  function shrinkPhoto(file, maxBytes) {
    if (!file || !maxBytes || file.size <= maxBytes) return Promise.resolve({ file: file, note: '' });
    return decodePhoto(file).then(function (img) {
      var w = img.width, h = img.height;
      var fit = fitCanvas(w, h);
      if (!fit) return { file: file, note: '' };
      var c = fit.c;
      var name = String(file.name || 'foto').replace(/\.[a-z0-9]{2,5}$/i, '') + '.jpg';
      function attempt(k) {
        c.width = Math.max(1, Math.round(w * k)); c.height = Math.max(1, Math.round(h * k));
        var g = c.getContext('2d'); g.imageSmoothingQuality = 'high';
        g.fillStyle = '#ffffff'; g.fillRect(0, 0, c.width, c.height);   // flatten PNG alpha onto white
        g.drawImage(img, 0, 0, c.width, c.height);
        return encodeJpeg(c, maxBytes, 0.92).then(function (b) {
          if (!b) return { file: file, note: '' };
          if (b.size > maxBytes && k > 0.2) return attempt(k * 0.85);
          var note = (k < 0.999 ? 'nedskaleret ' + w + 'x' + h + ' → ' + c.width + 'x' + c.height : 'fuld opløsning ' + w + 'x' + h + ', genkomprimeret') +
            ' (' + (Math.round(file.size / 104857.6) / 10) + ' → ' + (Math.round(b.size / 104857.6) / 10) + ' MB)';
          return { file: new File([b], name, { type: 'image/jpeg' }), note: note };
        });
      }
      return attempt(fit.k).then(function (out) { if (img.close) img.close(); c.width = c.height = 0; return out; });
    }).catch(function () { return { file: file, note: '' }; });
  }
  // Fit the customer's originals into what is left of the budget after the fixed
  // parts (print + preview). Small files stay untouched and hand their unused
  // share on to the larger ones.
  // Resolves to [{ file, note }] in the original order (null slots stay null).
  function fitPhotos(files, fixedBytes) {
    var avail = UPLOAD_BUDGET - (fixedBytes || 0);
    var total = 0, i;
    for (i = 0; i < files.length; i++) total += files[i] ? files[i].size : 0;
    var keep = function (f) { return f ? { file: f, note: '' } : null; };
    if (total <= avail) return Promise.resolve(files.map(keep));
    var order = files.map(function (f, idx) { return idx; }).filter(function (idx) { return !!files[idx]; })
      .sort(function (a, b) { return files[a].size - files[b].size; });
    var caps = {}, left = Math.max(avail, 256 * 1024 * order.length);
    for (i = 0; i < order.length; i++) {
      var cap = left / (order.length - i), f = files[order[i]];
      caps[order[i]] = f.size <= cap ? 0 : cap;
      left -= Math.min(f.size, cap);
    }
    return Promise.all(files.map(function (f, idx) {
      return caps[idx] ? shrinkPhoto(f, caps[idx]) : Promise.resolve(keep(f));
    }));
  }
  window.PeletonUpload = { BUDGET: UPLOAD_BUDGET, PRINT_MAX: PRINT_MAX, encodeJpeg: encodeJpeg, shrinkPhoto: shrinkPhoto, fitPhotos: fitPhotos };

  window.PeletonPosterRender = renderPoster;
  function boot() { initAll(); initCarousels(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
})();
